import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";


import { Guid } from "guid-typescript";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";

import {
  CoverageRange,
  RequestCriticalLevel
} from "digital-core";
import { DealerProductsType, FileInfo, IBranchDetails, IUserPreference, MarketingProspectsConsents, QuestionInfo } from "../models";
import { ConfigService } from "./config.service";
import { HeaderFactoryService } from "./header-factory.service";
import {
  ADPResourceService as ResourceService,
} from "digital-core";
import {
  ErrorLogService,
} from "common";

import {
  CCPaymentInfo,
  CustomerInfo,
  DeviceInfo, DeviceMake, DeviceModel,
  MultipleChoiceQuestion,
  PaymentMethodInfo,
  PaymentMethodTypes,
  PolicyInfo,
  PolicyResponse,
  PrePolicyModel,
  ProgressBarData,
  QuestionAnswer,
  SaleSteps,
  SalesActivity,
  SalesApplication,
  SearchDeviceInfo,
  SearchInfo,
  SelectedProduct,
  SigningModel,
  TrueFalseQuestion
} from "../models";

import { SerializationHelperService } from "./serialization-helper.service";
import { UserPreferenceService } from "./user-preferance.service";

@Injectable()
export class SalesService {
  private salesActivity: SalesActivity;
  private policySearchResults: PolicyInfo[];


  public paymentMethodObservable = new Subject<PaymentMethodInfo>();
  public customerInfoObservable = new BehaviorSubject<CustomerInfo>({} as CustomerInfo);
  public productInfoObservable = new BehaviorSubject<SelectedProduct>({} as SelectedProduct);
  public policyResponseObservable = new Subject<PolicyResponse>();
  public deviceSelectedObservable = new BehaviorSubject<string>('');
  public policySearchResultObservable = new Subject<PolicyInfo[]>();
  public progressStepCurrentValue = new BehaviorSubject<Number>(0);
  public resumeSavedSaleApplication = false;
  public salesActivityObservable: Observable<SalesActivity>;
  public existingSalesActivity = new BehaviorSubject<SalesActivity>({} as SalesActivity);
  public isCustomerInfoExist = false;


  constructor(
    private httpsClient: HttpClient,
    private headerService: HeaderFactoryService,
    private configService: ConfigService,
   
    private serializationHelperService: SerializationHelperService,
    private userPreferenceService: UserPreferenceService,
    private resourceService: ResourceService,
    private errorLogService: ErrorLogService,
  ) {
    this.salesActivity = new SalesActivity();
    this.salesActivity.SalesId = Guid.create().toString();
  }

  public getEligibilityQuestions(): Observable<TrueFalseQuestion[]> {
    return this.httpsClient.get<TrueFalseQuestion[]>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion
      }/sales/eligibilityQuestions`,
      this.headerService.getBasicHeaderOptions()
    );
  }

  public getUpdatedProgressStepValue(circularStepCurrentValue: Number) {
    this.progressStepCurrentValue.next(circularStepCurrentValue);
  }

  public getDemandsAndNeedsQuestions(): Observable<MultipleChoiceQuestion[]> {
    return this.httpsClient.get<MultipleChoiceQuestion[]>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion
      }/sales/demandAndNeedsQuestionAnswers`,
      this.headerService.getAuthorizationHeaderOptions()
    );
  }

  public saveEligibilityAnswers(answers: QuestionAnswer[]) {
    return this.httpsClient.post<boolean>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion
      }/sales/eligibility`,
      answers,
      this.headerService.getAuthorizationHeaderOptions()
    );
  }

  public saveDemandAndNeedsAnswers(answers: QuestionAnswer[]) {
    return this.httpsClient.post<boolean>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion
      }/sales/demandAndNeeds`,
      answers,
      this.headerService.getAuthorizationHeaderOptions()
    );
  }

  public savePaymentInfo(paymentInfo: PaymentMethodInfo) {
    if (paymentInfo.PaymentMethodType === PaymentMethodTypes.CreditCard) {
      let currentPayment = paymentInfo as CCPaymentInfo;
      const ccInfo = new CCPaymentInfo();
      ccInfo.CreditCardNumber = currentPayment.CreditCardNumber;
      ccInfo.CVVCode = currentPayment.CVVCode;
      ccInfo.ExpirationDate = currentPayment.ExpirationDate;
      ccInfo.NameOnCard = currentPayment.NameOnCard;
      ccInfo.PaymentMethodType = currentPayment.PaymentMethodType;
      this.salesActivity.PaymentMethodInfo = ccInfo;
      this.paymentMethodObservable.next(ccInfo);
    } else {
      this.salesActivity.PaymentMethodInfo = paymentInfo;
      this.paymentMethodObservable.next(paymentInfo);
    }
    this.salesActivity.PaymentInstrumentType = paymentInfo.PaymentInstrumentType;
    this.salesActivity.PaymentType = paymentInfo.PaymentType;
  }

  public savePolicyResponse(policyResponse: PolicyResponse) {
    this.salesActivity.PolicyResponse = policyResponse;
    this.policyResponseObservable.next(policyResponse);
  }

  public SetBranchCode(branchCode: string) {
    this.salesActivity.BranchCode = branchCode;
  }

  public SetSelectedInsuranceType(selectedInsuranceType: DealerProductsType) {
    this.salesActivity.SalesInsuranceType = selectedInsuranceType;
  }

  public savePolicyNumberAndPaymentReferenceNumber(prePolicy: PrePolicyModel) {
    this.salesActivity.PolicyNumber = prePolicy.PolicyNumber;
    this.salesActivity.PaymentReferenceNumber = prePolicy.PaymentReferenceNumber;
  }

  public clearPaymentInfo() {
    this.salesActivity.PaymentMethodInfo = null;
    this.paymentMethodObservable.next(null);
  }

  public clearPolicyInfo() {
    this.salesActivity.PolicyResponse = null;
    this.policyResponseObservable.next(null);
  }

  public resetSalesId() {
    this.salesActivity = new SalesActivity();
    this.salesActivity.SalesId = Guid.create().toString();
    this.customerInfoObservable.next({} as CustomerInfo);
    this.productInfoObservable.next({} as SelectedProduct);
    this.deviceSelectedObservable.next('');
    this.progressStepCurrentValue.next(0);
    this.resumeSavedSaleApplication = false;
  }

  public saveProductInfo(productInfo: SelectedProduct) {
    this.salesActivity.Product = productInfo;
    this.salesActivity.CertificateDuration = productInfo?.CertificateDuration || '0';
    this.salesActivity.CoverageStartOffset = productInfo?.CoverageStartOffset || '0';
    this.salesActivity.SalesPrice = productInfo?.SalesPrice || '0';

    this.productInfoObservable.next(this.salesActivity.Product);
  }

  public saveCustomerInfo(customerInfo: CustomerInfo) {
    const ccInfo = new CustomerInfo(
      customerInfo.LastName,
      customerInfo.FirstName,
      customerInfo.Address1,
      customerInfo.Address2,
      customerInfo.Address3,
      customerInfo.City,
      customerInfo.PostalCode,
      customerInfo.Province,
      customerInfo.DateOfBirth,
      this.getEmailFromSalesActivityIfNotExistOnCustomerInfo(customerInfo.Email),
      customerInfo.MaskCellNumber,
      customerInfo.CellNumber,
      customerInfo.Beneficiary,
      customerInfo.Salutation,
      customerInfo.Nationality,
      customerInfo.Country,
      customerInfo.Region,
      customerInfo.BirthCity
    );
    ccInfo.BirthPlace = customerInfo.BirthPlace;
    ccInfo.TaxId = customerInfo.TaxId;
    ccInfo.AccountNumber = customerInfo.AccountNumber;
    ccInfo.ExpiryDate = customerInfo.ExpiryDate;
    ccInfo.IdDocuments = customerInfo.IdDocuments;
    this.salesActivity.CustomerInfo = ccInfo;
    this.customerInfoObservable.next(this.salesActivity.CustomerInfo);
  }

  public saveQuestionAnswers(questionAnswers: QuestionInfo[]) {
    this.salesActivity.QuestionAnswer = questionAnswers;
  }

  public saveMarketingProspectsConsent(mpcList: MarketingProspectsConsents[]) {
    if (mpcList != null && mpcList != undefined && mpcList.length > 0) {
      this.salesActivity.MarketingProspectsConsentsCollection = mpcList;
    }
  }

  public saveCustomerDataConsent(customerDataConsent: boolean) {
    this.salesActivity.CustomerDataConsent = customerDataConsent;
  }

  public saveCustomerConsentCommunication(customerConsentCommunication: boolean) {
    this.salesActivity.CustomerConsentCommunication = customerConsentCommunication;
  }

  public saveMarketingTAndC(mpcList: MarketingProspectsConsents[]) {
    if (mpcList != null && mpcList != undefined && mpcList.length > 0) {
      this.salesActivity.MarketingTAndCCollections = mpcList;
    }
  }

  public saveLastSavedSection(saleStep: SaleSteps) {
    this.salesActivity.LastSavedSection = saleStep;
  }
  public saveProgressBarData(progressBar: ProgressBarData) {
    this.salesActivity.ProgressBarData = progressBar;
  }
  public saveSearchPlansResult(
    imei: string,
    deviceMake: DeviceMake,
    deviceModel: DeviceModel,
    purchasePrice: number,
    purchaseDate: string,
    invoiceNumber: string,
    invoiceDocuments: FileInfo[],
    isImei: string,
    isUpload: string,
    skuNumber: string,
    IsRefurbished: string,
    equipmentRiskType: string,
    equipment: string,
    barCode: string
  ) {
    this.salesActivity.IMEI = imei;
    this.salesActivity.DeviceInfo = new DeviceInfo(
      deviceMake,
      deviceModel,
      purchasePrice,
      purchaseDate,
      invoiceNumber,
      invoiceDocuments,
      isImei,
      isUpload,
      null,
      skuNumber,
      IsRefurbished,
      equipmentRiskType,
      equipment,
      barCode
    );
    if (deviceMake && deviceModel) {
      this.deviceSelectedObservable.next(
        `${deviceMake?.Name} ${deviceModel?.Name}`
      )
    }
    else if (equipment) {
      this.deviceSelectedObservable.next(
        `${equipment}`
      )
    }
  }

  public getSearchPlanDeviceInfo() {
    if (this.salesActivity) {
      let result: SearchDeviceInfo = {
        imei: this.salesActivity.IMEI ? this.salesActivity.IMEI : "",
        model: this.salesActivity.DeviceInfo.DeviceModel
          ? this.salesActivity.DeviceInfo.DeviceModel.Name
          : "",
        modelId: this.salesActivity.DeviceInfo.DeviceModel
          ? this.salesActivity.DeviceInfo.DeviceModel.Id
          : "",
        make: this.salesActivity.DeviceInfo.DeviceMake
          ? this.salesActivity.DeviceInfo.DeviceMake.Name
          : "",
        purchasePrice: this.salesActivity.DeviceInfo.PurchasePrice
          ? this.salesActivity.DeviceInfo.PurchasePrice
          : 0,
        purchaseDate: this.salesActivity.DeviceInfo.PurchaseDate
          ? this.salesActivity.DeviceInfo.PurchaseDate
          : "",
        invoiceNumber: this.salesActivity.DeviceInfo.InvoiceNumber
          ? this.salesActivity.DeviceInfo.InvoiceNumber
          : "",
        isImei: this.salesActivity.DeviceInfo.IsIMEI
          ? this.salesActivity.DeviceInfo.IsIMEI
          : "yes",
        isUpload: this.salesActivity.DeviceInfo.IsUpload
          ? this.salesActivity.DeviceInfo.IsUpload
          : "yes",
        isSerialNumber: this.salesActivity.DeviceInfo.IsSerialNumber
          ? this.salesActivity.DeviceInfo.IsSerialNumber
          : "yes",
        serialNumber: this.salesActivity.DeviceInfo.serialNumber
          ? this.salesActivity.DeviceInfo.serialNumber
          : "",
        DealerShipProgramType: this.salesActivity.DeviceInfo.DealerShipProgramType
          ? this.salesActivity.DeviceInfo.DealerShipProgramType
          : "",
        EquipmentRiskType: this.salesActivity.DeviceInfo.EquipmentRiskType
          ? this.salesActivity.DeviceInfo.EquipmentRiskType
          : "",
        Equipment: this.salesActivity.DeviceInfo.Equipment
          ? this.salesActivity.DeviceInfo.Equipment
          : "",
        Barcode: this.salesActivity.DeviceInfo.BarCode
          ? this.salesActivity.DeviceInfo.BarCode
          : ""
      };
      return result;
    } else {
      let result = new SearchDeviceInfo();
      result.imei = "";
      result.make = "";
      result.model = "";
      result.purchasePrice = null;
      result.purchaseDate = "";
      result.invoiceNumber = "";
      return result;
    }
  }

  public getSalesActivity() {
    return this.salesActivity;
  }

  public setSalesActivity(salesActivity: SalesActivity) {
    this.salesActivity = {
      ...salesActivity
    }
  }

  public setSalesActivityEmail(email: string) {
    this.salesActivity.Email = email ? email : '';
  }
  public getSigningUrl(salesActivity: SalesActivity) {

    const formData = new FormData();
    formData.append('salesActivity', JSON.stringify(salesActivity));

    return this.httpsClient.post<SigningModel>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion
      }/Signature/signingUrl`,
      formData,
      this.headerService.getAuthorizationHeaderOptions(
        null, RequestCriticalLevel.Critical
      )
    )
  }

  public getReceiptDoc(salesActivity: SalesActivity): Observable<BlobPart> {
    const formData = new FormData();
    formData.append('salesActivity', JSON.stringify(salesActivity));
    return this.httpsClient.post<BlobPart>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion
      }/Signature/receiptDoc`,
      formData,
      this.headerService.getBasicHeaderOption(true)
    );
  }

  public getContractDoc(userPreference?: IUserPreference, planProductDocumentType?: string): Observable<BlobPart> {
    if (!!userPreference?.selectedInsuranceType) {
      if (!!planProductDocumentType) {
        return this.httpsClient.get<BlobPart>(
          `${this.configService.apiUrl}api/v${this.configService.apiVersion
          }/Signature/contractDoc/${userPreference.selectedInsuranceType}/${planProductDocumentType}`,
          this.headerService.getBasicHeaderOption(true)
        );
      } else {
        return this.httpsClient.get<BlobPart>(
          `${this.configService.apiUrl}api/v${this.configService.apiVersion
          }/Signature/contractDoc/${userPreference.selectedInsuranceType}`,
          this.headerService.getBasicHeaderOption(true)
        );
      }
    } else {
      return this.httpsClient.get<BlobPart>(
        `${this.configService.apiUrl}api/v${this.configService.apiVersion
        }/Signature/contractDoc/1`,
        this.headerService.getBasicHeaderOption(true)
      );
    }
  }

  public searchPolicy(searchInfo: SearchInfo): Observable<PolicyInfo[]> {
    return this.httpsClient.get<PolicyInfo[]>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion
      }/policy/search/${searchInfo.SearchCriteria}/${searchInfo.SearchValue}/${searchInfo.RemoveCancelled}/${searchInfo.FilterWithDealer}`,
      this.headerService.getAuthorizationHeaderOptions()
    );
  }

  public updatePolicyCancel(searchInfo: SearchInfo): Observable<PolicyInfo[]> {
    return this.httpsClient.get<PolicyInfo[]>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion
      }/policy/updateCancelStatus/${searchInfo.SearchCriteria}/${searchInfo.SearchValue}`,
      this.headerService.getAuthorizationHeaderOptions()
    );
  }

  saveMobileSalesActivity(): void {
    if (!this.salesActivity) {
      return;
    }
    let salesActivityCopy: SalesActivity = JSON.parse(JSON.stringify(this.salesActivity));
    let policyNumber = salesActivityCopy.PolicyResponse?.PolicyNumber;
    let activity: SalesActivity = {
      ...salesActivityCopy,
      PaymentReferenceNumber: '',
      PaymentType: '',
      PolicyDocuments: []
    };

    if (this.salesActivity.IsPaymentGateway) {
      activity.PolicyNumber = policyNumber != null ? policyNumber : salesActivityCopy.PolicyNumber;
    } else {
      activity.PolicyNumber = policyNumber;
    }

    const formData = new FormData();
    formData.append('salesActivity', JSON.stringify(activity));
    this.httpsClient.post<string>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion}/sales/saveSalesActivity`,
      formData,
      this.headerService.getAuthorizationHeaderOptions(null, RequestCriticalLevel.Critical)
    ).subscribe(x => { });
  }


  public savePolicySearchResults(policyResults: PolicyInfo[]): void {
    this.policySearchResults = policyResults as PolicyInfo[];
    this.policySearchResultObservable.next(policyResults);
  }

  public getPolicySearchResults(): PolicyInfo[] {
    return this.policySearchResults;
  }

  public saveSignaturePackageId(packageId: string) {
    this.salesActivity.SignaturePackageId = packageId;
  }

  public retrieveSalesApplications(searchCriteria?: { [name: string]: string }) {
    const options = {
      ...this.headerService.getAuthorizationHeaderOptions(),
      params: searchCriteria
    }
    return this.httpsClient
      .get<SalesApplication[]>(
        `${this.configService.apiUrl}api/v${this.configService.apiVersion}/sales`,
        options
      );
  }
  public retrieveSaleApplication(saleApplicationId: string) {
    return this.httpsClient.get<SalesApplication>(
      `${this.configService.apiUrl}api/v${this.configService.apiVersion}/sales/${saleApplicationId}`,
      this.headerService.getAuthorizationHeaderOptions()
    );
  }
  public setSaleApplicationLocally(_saleApplication: SalesApplication) {
    if (_saleApplication) {
      this.salesActivity = this.serializationHelperService.toInstance(
        this.salesActivity,
        _saleApplication.Data
      );

      if (this.salesActivity.SalesInsuranceType) {
        let userPreference = { selectedInsuranceType: this.salesActivity.SalesInsuranceType };
        this.userPreferenceService.setUserPreferenceLocalStorage(userPreference);
      }

      if (this.salesActivity.DeviceInfo) {
        if (this.salesActivity.DeviceInfo.Equipment) {
          this.deviceSelectedObservable.next(
            `${this.salesActivity.DeviceInfo.Equipment}`
          );

        }
        else if (this.salesActivity.DeviceInfo.DeviceMake.Name && this.salesActivity.DeviceInfo.DeviceModel.Name) {
          this.deviceSelectedObservable.next(
            `${this.salesActivity.DeviceInfo.DeviceMake.Name} ${this.salesActivity.DeviceInfo.DeviceModel.Name}`
          );
        }
      }
      if (this.salesActivity.Product) {
        this.saveProductInfo(this.salesActivity.Product);
      }
      this.saveMarketingProspectsConsent(this.salesActivity.MarketingProspectsConsentsCollection);
      if (this.salesActivity.CustomerInfo) {
        this.customerInfoObservable.next(this.salesActivity.CustomerInfo);
      }
      if (this.salesActivity.LastSavedSection) {
        this.progressStepCurrentValue.next(this.salesActivity.LastSavedSection);
      }
      this.salesActivityObservable = of(this.salesActivity);
      this.resumeSavedSaleApplication = true;
    }
  }
  public getEmailFromSalesActivityIfNotExistOnCustomerInfo(Email: string): string {
    if (Email) {
      return Email;
    } else {
      if (this.salesActivity.Email) {
        return this.salesActivity.Email;
      }
      return '';
    }
  }

  setCustomerInfo(customerInfo: CustomerInfo) {
    this.salesActivity.CustomerInfo = customerInfo;
    //this.customerInfo = customerInfo;
    this.isCustomerInfoExist = true;
  }

  setExistingPolicySalesActivity(salesActivity: SalesActivity) {
    this.existingSalesActivity.next(salesActivity);
  }

  setIsPaymentGateway(isTerashop: boolean) {
    this.salesActivity.IsPaymentGateway = isTerashop;
  }

  public SetBranchDetail(branchDetails: IBranchDetails) {
    this.salesActivity.BranchDetails = branchDetails;
  }

  formatAddress(customerInfo: CustomerInfo) {
    if (!customerInfo) { return ""; }
    let setting = this.resourceService.getPortalSetting("Client_AddressFormat");

    if (setting != null && customerInfo !== undefined) {
      let addressTemplate = setting.Value;

      addressTemplate = addressTemplate.replace("{Address1}", customerInfo.Address1);

      if (customerInfo.Address2 !== null && customerInfo.Address2 !== "") {
        addressTemplate = addressTemplate.replace("{Address2}", customerInfo.Address2);
      } else {
        addressTemplate = addressTemplate.replace("{Address2}", "");
      }

      addressTemplate = addressTemplate.replace("{City}", customerInfo.City);

      if (customerInfo.PostalCode !== null && customerInfo.PostalCode !== "") {
        addressTemplate = addressTemplate.replace("{PostalCode}", customerInfo.PostalCode);
      }

      if (customerInfo.Address3 !== null) {
        addressTemplate = addressTemplate.replace("{Address3}", customerInfo.Address3);
      } else {
        addressTemplate = addressTemplate.replace("{Address3}", "");
      }

      if (customerInfo.Region !== null) {
        addressTemplate = addressTemplate.replace("{Region}", customerInfo.Region);
      }

      if (customerInfo.Country !== null && customerInfo.Country !== "") {
        this.resourceService
          .getListItemResource("CustomerInformation_List_Nationality")
          .subscribe(
            responseNationalities => {
              if (customerInfo.Country) {
                let customerCountry = responseNationalities.find(
                  c => c.Key === customerInfo.Country
                );
                if (customerCountry) {
                  addressTemplate = addressTemplate.replace("{Country_Code}", customerCountry.Value.split('&uacute;').join('ú')
                    ? customerCountry.Value.split('&uacute;').join('ú')
                    : "")
                } else {
                  this.errorLogService.showInfo("Country not found", true);
                }
              }
            },
            error => {
              this.errorLogService.logError(error);
            }
          );
      }

      addressTemplate = addressTemplate.replace("  ", " ");
      addressTemplate = addressTemplate.replace(" ,", ",");
      addressTemplate = addressTemplate.replace(",,", ",");

      return addressTemplate;
    }
    return "";
  }

  public getPriceRange() {
    // Rounding coverage range values to avoid validator triggering false positives
    const _min = Number(
      this.resourceService.getPortalSetting("DevicePrice_MinValue").Value
    );
    const _max = Number(
      this.resourceService.getPortalSetting("DevicePrice_MaxValue").Value
    );
    const _coverageRange = new CoverageRange(_min, _max);
    return _coverageRange;
  }
}